import React from 'react';
import HelpBadge from './components/HelpBadge';
import './UxOpsAdmin.css';

type GoldenSignals = {
  availability_pct?: number;
  error_rate_pct?: number;
  p95_latency_ms?: number;
  traffic_rps?: number;
};

type OpsOverview = {
  observability?: {
    status?: string;
    golden_signals?: GoldenSignals;
    slo_targets?: {
      availability_pct_target?: number;
      latency_p95_ms_target?: number;
      error_rate_pct_target?: number;
    };
  };
};

type OpsAlert = {
  name: string;
  severity: string;
  firing: boolean;
  summary: string;
};

type SiteTrafficOverview = {
  window_hours?: number;
  pageviews?: number;
  sessions?: number;
  visitors?: number;
  top_paths?: Array<{ path: string; hits: number }>;
  by_day?: Array<{ day: string; hits: number }>;
  note?: string;
};

type ObservabilityAdminSectionProps = {
  gatewayBaseUrl: string;
  metricsServiceUrl: string;
  accessToken: string | null;
};

const localHost = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

const ObservabilityAdminSection: React.FC<ObservabilityAdminSectionProps> = ({
  gatewayBaseUrl,
  metricsServiceUrl,
  accessToken,
}) => {
  const [overview, setOverview] = React.useState<OpsOverview | null>(null);
  const [alerts, setAlerts] = React.useState<OpsAlert[]>([]);
  const [siteTraffic, setSiteTraffic] = React.useState<SiteTrafficOverview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const grafanaUrl = (process.env.REACT_APP_GRAFANA_URL || (localHost ? 'http://localhost:3001' : '')).trim();
  const prometheusUrl = (process.env.REACT_APP_PROMETHEUS_URL || (localHost ? 'http://localhost:9090' : '')).trim();
  const cloudwatchHint = (process.env.REACT_APP_CLOUDWATCH_OPS_DASHBOARD || 'nextcompli-{env}-ops').trim();

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      const [overviewResp, alertsResp, trafficResp] = await Promise.all([
        fetch(`${metricsServiceUrl}/metrics/ops-overview`, { headers }),
        fetch(`${metricsServiceUrl}/metrics/alerts`, { headers }),
        fetch(`${gatewayBaseUrl}/api/v1/site-traffic/overview?hours=168`, { headers }),
      ]);
      if (overviewResp.ok) {
        setOverview(await overviewResp.json() as OpsOverview);
      } else {
        setOverview(null);
        setError(`Ops overview unavailable (HTTP ${overviewResp.status}).`);
      }
      if (alertsResp.ok) {
        const payload = await alertsResp.json() as { alerts?: OpsAlert[] };
        setAlerts(Array.isArray(payload.alerts) ? payload.alerts : []);
      }
      if (trafficResp.ok) {
        setSiteTraffic(await trafficResp.json() as SiteTrafficOverview);
      } else {
        setSiteTraffic(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load observability signals.');
    }
  }, [accessToken, gatewayBaseUrl, metricsServiceUrl]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const signals = overview?.observability?.golden_signals || {};
  const targets = overview?.observability?.slo_targets || {};
  const firing = alerts.filter((alert) => alert.firing);

  return (
    <div className="theme-admin-card ux-ops-card">
      <div className="admin-console-toolbar">
        <div>
          <h2>
            Observability
            <HelpBadge label="Help about platform observability">
              Platform health for gateway, metrics, and list services: JSON logs, Prometheus RED metrics,
              optional traces, and CloudWatch/Grafana alerts. Customer compliance alerts stay on a separate SNS path.
            </HelpBadge>
          </h2>
          <p>Find logs, golden signals, SLO alerts, and the local Grafana/Prometheus stack from this page.</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => void load()}>Refresh</button>
      </div>

      {error ? <p className="admin-console-error" role="alert">{error}</p> : null}

      <div className="ux-ops-grid">
        <article className="ux-ops-stat">
          <p>Status</p>
          <strong>{overview?.observability?.status || 'unknown'}</strong>
        </article>
        <article className="ux-ops-stat">
          <p>Availability</p>
          <strong>{(signals.availability_pct ?? 0).toFixed(1)}%</strong>
          <span>SLO {(targets.availability_pct_target ?? 99).toFixed(1)}%</span>
        </article>
        <article className="ux-ops-stat">
          <p>Error rate</p>
          <strong>{(signals.error_rate_pct ?? 0).toFixed(2)}%</strong>
          <span>SLO {(targets.error_rate_pct_target ?? 1).toFixed(2)}%</span>
        </article>
        <article className="ux-ops-stat">
          <p>P95 latency</p>
          <strong>{(signals.p95_latency_ms ?? 0).toFixed(0)} ms</strong>
          <span>SLO {(targets.latency_p95_ms_target ?? 1000).toFixed(0)} ms</span>
        </article>
      </div>

      <h3>Site traffic (nextcompli.com)</h3>
      <p className="theme-admin-message">
        {siteTraffic?.note
          || 'Razzmatazz owns full web analytics. This card is a ops snapshot — open Marketing via Razzmatazz → Traffic for the full view.'}
      </p>
      <div className="ux-ops-grid">
        <article className="ux-ops-stat">
          <p>Visitors (7d)</p>
          <strong>{siteTraffic?.visitors ?? siteTraffic?.sessions ?? 0}</strong>
        </article>
        <article className="ux-ops-stat">
          <p>Pageviews (7d)</p>
          <strong>{siteTraffic?.pageviews ?? 0}</strong>
        </article>
        <article className="ux-ops-stat">
          <p>Sessions (7d)</p>
          <strong>{siteTraffic?.sessions ?? 0}</strong>
        </article>
      </div>
      <p className="theme-admin-message">
        <a className="access-management-splash-link" href="/console/razzmatazz">
          <strong>Open Razzmatazz → Traffic</strong>
          <span>Channels, UTM, devices, bounce rate, and day-by-day.</span>
        </a>
      </p>
      {(siteTraffic?.top_paths || []).length > 0 ? (
        <ul className="ux-ops-alerts">
          {(siteTraffic?.top_paths || []).slice(0, 10).map((row) => (
            <li key={row.path}>
              <strong>{row.path}</strong>
              <span>{row.hits}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="theme-admin-message">
          No pageviews yet. Open nextcompli.com in a private window (not /console), then refresh Razzmatazz → Traffic.
        </p>
      )}

      <h3>Where to look</h3>
      <div className="ux-ops-tools">
        {grafanaUrl ? (
          <a className="access-management-splash-link" href={grafanaUrl} target="_blank" rel="noreferrer">
            <strong>Grafana</strong>
            <span>SLO dashboard. Local default is admin / nextcompli on port 3001.</span>
          </a>
        ) : null}
        {prometheusUrl ? (
          <a className="access-management-splash-link" href={prometheusUrl} target="_blank" rel="noreferrer">
            <strong>Prometheus</strong>
            <span>Scrape targets and raw RED metrics.</span>
          </a>
        ) : null}
        <a className="access-management-splash-link" href={`${gatewayBaseUrl}/metrics`} target="_blank" rel="noreferrer">
          <strong>Gateway /metrics</strong>
          <span>Prometheus scrape for the API gateway.</span>
        </a>
        <div className="access-management-splash-link">
          <strong>CloudWatch ops dashboard</strong>
          <span>{cloudwatchHint}. SNS topic nextcompli-{'{env}'}-ops-alerts.</span>
        </div>
        <a className="access-management-splash-link" href="/console/admin/experience">
          <strong>User experience</strong>
          <span>Failed logins, uploads, abandoned applications, and mobile bounce.</span>
        </a>
      </div>

      <h3>SLO alerts</h3>
      {firing.length === 0 ? (
        <p className="theme-admin-message">No firing SLO alerts from metrics-service.</p>
      ) : (
        <ul className="ux-ops-alerts">
          {firing.map((alert) => (
            <li key={alert.name}>
              <strong>{alert.name}</strong>
              <span>{alert.severity}</span>
              <p>{alert.summary}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ObservabilityAdminSection;
